import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/context/AuthContext";

export default function LearningPreferences() {
  const [catalogue, setCatalogue] = useState({ keys: [], labels: {}, other_notes_max_length: 1000 });
  const [selections, setSelections] = useState([]);
  const [otherNotes, setOtherNotes] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const load = () => {
    setLoading(true);
    setLoadError("");
    return Promise.all([api.get("/learning-preference-catalogue"), api.get("/learner/learning-preferences")])
      .then(([catRes, prefRes]) => {
        setCatalogue(catRes.data);
        // Response shape is always { preferences: {...} } — nested, never flattened.
        const prefs = prefRes.data?.preferences || {};
        setSelections(prefs.selections || []);
        setOtherNotes(prefs.other_notes || "");
        setConsent(!!prefs.consent_acknowledged);
      })
      .catch((err) => setLoadError(err?.response?.data?.detail || "Could not load learning preferences."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (key) => {
    setSelections((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const save = async () => {
    setSaving(true);
    setMessage("");
    setError("");
    try {
      const res = await api.put("/learner/learning-preferences", {
        selections,
        other_notes: otherNotes,
        consent_acknowledged: consent,
      });
      const prefs = res.data.preferences;
      setSelections(prefs.selections || []);
      setOtherNotes(prefs.other_notes || "");
      setConsent(!!prefs.consent_acknowledged);
      setMessage("Learning preferences saved.");
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not save learning preferences.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p data-testid="learning-preferences-loading">Loading…</p>;
  }

  if (loadError) {
    return (
      <div className="space-y-4" data-testid="learning-preferences-load-error">
        <h1 className="text-3xl font-bold text-primary">Learning preferences</h1>
        <p role="alert" className="text-destructive" data-testid="preferences-error">
          {loadError}
        </p>
        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn-outline min-h-11" onClick={load} data-testid="learning-preferences-retry">
            Try again
          </button>
          <Link to="/dashboard" className="underline self-center">
            Back to dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="learning-preferences-page">
      <header className="space-y-1">
        <h1 className="text-3xl font-bold text-primary">Learning preferences</h1>
        <p className="text-muted-foreground">
          Tell your instructor what helps you learn best. This is optional and never affects your scores or readiness.
        </p>
      </header>

      <section className="card space-y-2" data-testid="learning-preferences-disclaimer">
        <p className="text-sm">
          This is not a medical or diagnostic form. Please do not share diagnoses, conditions, or medication
          details here — only what helps during a driving lesson.
        </p>
      </section>

      <section className="card space-y-3" data-testid="learning-preferences-options">
        <h2 className="font-semibold">What helps you learn?</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {catalogue.keys.map((key) => (
            <label key={key} className="flex items-start gap-2 min-h-11" data-testid={`preference-option-${key}`}>
              <input
                type="checkbox"
                className="mt-1"
                checked={selections.includes(key)}
                onChange={() => toggle(key)}
              />
              <span>{catalogue.labels[key] || key}</span>
            </label>
          ))}
        </div>
      </section>

      <section className="card space-y-2" data-testid="learning-preferences-other">
        <label className="block space-y-1">
          <span className="font-medium">Anything else that helps you learn safely and confidently?</span>
          <textarea
            className="w-full border rounded px-3 py-2 min-h-24"
            value={otherNotes}
            onChange={(e) => setOtherNotes(e.target.value)}
            maxLength={catalogue.other_notes_max_length}
            rows={4}
            placeholder="Optional — please do not include diagnoses or medication details."
            data-testid="preference-other-notes"
          />
        </label>
        <p className="text-xs text-muted-foreground">
          {otherNotes.length}/{catalogue.other_notes_max_length}
        </p>
      </section>

      <section className="card space-y-2">
        <label className="flex items-start gap-2">
          <input
            type="checkbox"
            className="mt-1"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            data-testid="preference-consent"
          />
          <span className="text-sm">
            I understand this information will be shared with my assigned instructor to support my lessons, and
            is not a medical record.
          </span>
        </label>
      </section>

      <div className="flex flex-wrap gap-2 items-center">
        <button
          type="button"
          className="btn-accent min-h-11"
          onClick={save}
          disabled={saving}
          data-testid="preferences-save"
        >
          {saving ? "Saving…" : "Save preferences"}
        </button>
        {message && (
          <p role="status" data-testid="preferences-message">
            {message}
          </p>
        )}
        {error && (
          <p role="alert" className="text-destructive" data-testid="preferences-error">
            {error}
          </p>
        )}
      </div>

      <p>
        <Link to="/dashboard" className="underline">
          Back to dashboard
        </Link>
      </p>
    </div>
  );
}
